import { notifyError } from "@/lib/notify-error";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { Bell, CheckCheck } from "lucide-react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AppLink } from "@/components/app-link";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toTitleCase } from "@/lib/formatting";
import { formatDate } from "@/lib/utils";

export const Route = createFileRoute("/inbox")({
  component: InboxPage,
});

export function InboxPage() {
  const { data: inbox } = useQuery(convexQuery(api.notifications.listInbox, {
    paginationOpts: { numItems: 100, cursor: null },
  }));
  const markRead = useMutation(api.notifications.markRead);

  const handleMarkRead = async (notificationId: Id<"notifications">) => {
    try {
      await markRead({ notificationId });
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to mark notification as read");
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Inbox" />

      {inbox === undefined ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">Loading inbox...</CardContent>
        </Card>
      ) : inbox.page.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            No notifications yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {inbox.page.map((row) => {
            const payload = row.payload as
              | {
                  revisionId?: Id<"projectDocumentRevisions">;
                  preview?: string;
                  projectDocumentId?: Id<"projectDocuments">;
                  documentName?: string;
                  tableCount?: number;
                }
              | undefined;
            const isScheduleReview = row.type === "schedule_extraction_review";

            return (
              <Card key={row._id} className={row.isRead ? "opacity-70" : ""}>
                <CardContent className="flex items-start justify-between gap-3 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Bell className="size-4 text-muted-foreground" aria-hidden="true" />
                      <p className="text-sm font-medium">
                        {row.type === "revcollab_mention"
                          ? `${row.actor?.name ?? "Someone"} mentioned you`
                          : isScheduleReview
                            ? "Schedule tables ready for review"
                            : toTitleCase(row.type)}
                      </p>
                      {!row.isRead ? (
                        <Badge variant="secondary" size="sm">
                          Unread
                        </Badge>
                      ) : null}
                    </div>
                    {isScheduleReview && payload?.documentName ? (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {payload.documentName} — {payload.tableCount ?? 0} table
                        {(payload.tableCount ?? 0) === 1 ? "" : "s"} extracted. Review and edit
                        the master tables before use.
                      </p>
                    ) : payload?.preview ? (
                      <p className="text-sm text-muted-foreground line-clamp-2">{payload.preview}</p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</p>
                    {isScheduleReview && payload?.projectDocumentId ? (
                      <AppLink
                        to="/project/documents/$documentId/scheduleTables"
                        params={{ documentId: payload.projectDocumentId }}
                        search={
                          payload.revisionId ? { revisionId: payload.revisionId } : undefined
                        }
                        title="Edit Schedule Tables"
                        className="text-xs font-medium text-primary hover:underline"
                        onClick={() => {
                          if (!row.isRead) void handleMarkRead(row._id);
                        }}
                      >
                        Review schedule tables
                      </AppLink>
                    ) : row.type === "revcollab_mention" && payload?.revisionId ? (
                      <AppLink
                        to="/revCollab/revisions/$revisionId"
                        params={{ revisionId: payload.revisionId }}
                        title="CollabPilot session"
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Open CollabPilot thread
                      </AppLink>
                    ) : null}
                  </div>
                  {!row.isRead ? (
                    <Button variant="ghost" size="sm" onClick={() => void handleMarkRead(row._id)}>
                      <CheckCheck className="size-4" />
                      Mark read
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
